import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { IoClose, IoAlertCircle, IoWarning, IoTime } from 'react-icons/io5';
import { getAllPartners } from '../../../services/memberService';
import {
  getComplaintSummaryByPartner,
  COMPLAINT_CATEGORIES,
  AUTO_SUSPEND_THRESHOLD,
} from '../../../services/complaintService';
import { listOverduePartners } from '../../../services/partnerDashboardService';

const fmtAmt = (v) => Number(v || 0).toLocaleString();

const AttentionItems = () => {
  const [tab, setTab] = useState('suspended');
  const [partners, setPartners] = useState([]);
  const [overdueRows, setOverdueRows] = useState([]); // [{partnerId, overdueCount, overdueTotal, overdueHours}]
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // { partner, summary }
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [all, overdue] = await Promise.all([
          getAllPartners(),
          listOverduePartners().catch(() => []),
        ]);
        setPartners(all);
        setOverdueRows(overdue);
      } catch (err) {
        console.error('파트너 로딩 실패:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const suspendedPartners = useMemo(
    () => partners
      .filter((p) => p.status === 'suspended')
      .sort((a, b) => (Number(b.penalty_total) || 0) - (Number(a.penalty_total) || 0)),
    [partners]
  );

  // 주의 파트너 — 벌점 누적 중이지만 아직 정지 안 된 파트너 (5점 이상 && 임계값 미만)
  const warningPartners = useMemo(
    () => partners
      .filter((p) => p.status === 'active' && (Number(p.penalty_total) || 0) >= 5 && (Number(p.penalty_total) || 0) < AUTO_SUSPEND_THRESHOLD)
      .sort((a, b) => (Number(b.penalty_total) || 0) - (Number(a.penalty_total) || 0)),
    [partners]
  );

  // 미수금 24h 초과 파트너 — partnerId로 partner 정보 머지
  const overduePartners = useMemo(() => {
    const partnerById = new Map(partners.map((p) => [p.uid, p]));
    return overdueRows
      .map((row) => {
        const p = partnerById.get(row.partnerId);
        if (!p) return null;
        return { ...p, ...row };
      })
      .filter(Boolean)
      .sort((a, b) => b.overdueHours - a.overdueHours);
  }, [partners, overdueRows]);

  const openDetail = async (p) => {
    setDetail({ partner: p, summary: null });
    setDetailLoading(true);
    try {
      const summary = await getComplaintSummaryByPartner(p.uid);
      setDetail({ partner: p, summary });
    } catch (err) {
      console.error('불만 현황 로딩 실패:', err);
      setDetail({ partner: p, summary: null });
    } finally {
      setDetailLoading(false);
    }
  };

  const renderList = (list, emptyText) => {
    if (loading) return <EmptyText>로딩 중...</EmptyText>;
    if (list.length === 0) return <EmptyText>{emptyText}</EmptyText>;
    return (
      <CardList>
        {list.map((p) => (
          <PartnerCard key={p.uid} onClick={() => openDetail(p)}>
            <CardLeft>
              <PartnerName>{p.businessName || p.name || '-'}</PartnerName>
              <PartnerSub>{p.email} · {p.phone || '-'}</PartnerSub>
              {p.suspendReason && <SuspendReason>{p.suspendReason}</SuspendReason>}
            </CardLeft>
            <CardRight>
              <PenaltyBadge $critical={(Number(p.penalty_total) || 0) >= AUTO_SUSPEND_THRESHOLD}>
                누적 벌점 {Number(p.penalty_total) || 0}점
              </PenaltyBadge>
              {p.status === 'suspended' && <StatusBadge>정지</StatusBadge>}
            </CardRight>
          </PartnerCard>
        ))}
      </CardList>
    );
  };

  const renderOverdueList = (list, emptyText) => {
    if (loading) return <EmptyText>로딩 중...</EmptyText>;
    if (list.length === 0) return <EmptyText>{emptyText}</EmptyText>;
    return (
      <CardList>
        {list.map((p) => (
          <PartnerCard key={p.uid} onClick={() => openDetail(p)}>
            <CardLeft>
              <PartnerName>{p.businessName || p.name || '-'}</PartnerName>
              <PartnerSub>{p.email} · {p.phone || '-'}</PartnerSub>
              <OverdueAmount>미수 {fmtAmt(p.overdueTotal)}원 ({p.overdueCount}건)</OverdueAmount>
            </CardLeft>
            <CardRight>
              <OverdueBadge>{p.overdueHours}시간 경과</OverdueBadge>
              {p.status === 'suspended' && <StatusBadge>정지</StatusBadge>}
            </CardRight>
          </PartnerCard>
        ))}
      </CardList>
    );
  };

  return (
    <Container>
      <TopRow>
        <Title>이상 파트너 관리</Title>
      </TopRow>

      <Tabs>
        <TabBtn $active={tab === 'suspended'} onClick={() => setTab('suspended')}>
          <IoAlertCircle size={14} color={tab === 'suspended' ? '#EF4444' : '#9CA3AF'} />
          정지 파트너 관리 <TabCount>({suspendedPartners.length})</TabCount>
        </TabBtn>
        <TabBtn $active={tab === 'warning'} onClick={() => setTab('warning')}>
          <IoWarning size={14} color={tab === 'warning' ? '#F59E0B' : '#9CA3AF'} />
          주의 파트너 <TabCount>({warningPartners.length})</TabCount>
        </TabBtn>
        <TabBtn $active={tab === 'overdue'} onClick={() => setTab('overdue')}>
          <IoTime size={14} color={tab === 'overdue' ? '#F97316' : '#9CA3AF'} />
          미수 24h+ <TabCount>({overduePartners.length})</TabCount>
        </TabBtn>
      </Tabs>

      {tab === 'suspended' && (
        <>
          <SectionNote>
            누적 벌점이 <b>{AUTO_SUSPEND_THRESHOLD}점</b> 이상에 도달하여 자동 정지된 파트너입니다. 파트너 카드를 클릭하면 해당 업체의 불만 현황을 확인할 수 있습니다.
          </SectionNote>
          {renderList(suspendedPartners, '정지된 파트너가 없습니다.')}
        </>
      )}

      {tab === 'warning' && (
        <>
          <SectionNote>
            누적 벌점 5점 이상 · {AUTO_SUSPEND_THRESHOLD}점 미만의 파트너입니다. 추가 벌점 발생 시 자동 정지 대상이 됩니다.
          </SectionNote>
          {renderList(warningPartners, '주의 대상 파트너가 없습니다.')}
        </>
      )}

      {tab === 'overdue' && (
        <>
          <SectionNote>
            미수금이 발생한 지 <b>24시간 이상</b> 결제되지 않은 파트너입니다. 해당 파트너는 DB 마켓 구매가 자동 차단됩니다.
          </SectionNote>
          {renderOverdueList(overduePartners, '24시간 초과 미수금 파트너가 없습니다.')}
        </>
      )}

      {/* 불만 현황 상세 모달 */}
      {detail && (
        <ModalOverlay onClick={() => setDetail(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{detail.partner.businessName || detail.partner.name || '-'} 불만 현황</ModalTitle>
              <XBtn onClick={() => setDetail(null)}><IoClose size={22} /></XBtn>
            </ModalHeader>
            <ModalBody>
              <MetaGrid>
                <MetaItem><MetaLabel>이메일</MetaLabel><MetaValue>{detail.partner.email || '-'}</MetaValue></MetaItem>
                <MetaItem><MetaLabel>연락처</MetaLabel><MetaValue>{detail.partner.phone || '-'}</MetaValue></MetaItem>
                <MetaItem><MetaLabel>상태</MetaLabel><MetaValue>{detail.partner.status === 'suspended' ? '정지' : '활성'}</MetaValue></MetaItem>
                <MetaItem><MetaLabel>누적 벌점</MetaLabel><MetaValue style={{ color: '#EF4444', fontWeight: 600 }}>{Number(detail.partner.penalty_total) || 0}점</MetaValue></MetaItem>
              </MetaGrid>

              {detailLoading ? (
                <EmptyText style={{ padding: '30px 0' }}>불만 현황 로딩 중...</EmptyText>
              ) : detail.summary ? (
                <>
                  <SectionTitle>민원 요약</SectionTitle>
                  <StatRow>
                    <StatCard><StatLabel>전체</StatLabel><StatValue>{detail.summary.total}</StatValue></StatCard>
                    <StatCard><StatLabel>대기</StatLabel><StatValue $color="#F59E0B">{detail.summary.pending}</StatValue></StatCard>
                    <StatCard><StatLabel>처리중</StatLabel><StatValue $color="#3B82F6">{detail.summary.processing}</StatValue></StatCard>
                    <StatCard><StatLabel>완료</StatLabel><StatValue $color="#22C55E">{detail.summary.resolved}</StatValue></StatCard>
                    <StatCard><StatLabel>반려</StatLabel><StatValue $color="#9CA3AF">{detail.summary.dismissed}</StatValue></StatCard>
                  </StatRow>

                  {Object.keys(detail.summary.byCategory || {}).length > 0 && (
                    <>
                      <SectionTitle style={{ marginTop: 18 }}>카테고리별 분포</SectionTitle>
                      <CategoryList>
                        {Object.entries(detail.summary.byCategory).map(([k, v]) => (
                          <CategoryChip key={k}>
                            {COMPLAINT_CATEGORIES[k] || k} <b>{v}건</b>
                          </CategoryChip>
                        ))}
                      </CategoryList>
                    </>
                  )}

                  {detail.summary.recent && detail.summary.recent.length > 0 && (
                    <>
                      <SectionTitle style={{ marginTop: 18 }}>최근 민원 (최대 5건)</SectionTitle>
                      <RecentList>
                        {detail.summary.recent.map((c) => (
                          <RecentItem key={c.id}>
                            <RecentDate>{c.date || c.createdAt || '-'}</RecentDate>
                            <RecentSummary>{c.summary || c.content || '내용 없음'}</RecentSummary>
                            {typeof c.penalty === 'number' && c.penalty > 0 && (
                              <RecentPenalty>+{c.penalty}점</RecentPenalty>
                            )}
                          </RecentItem>
                        ))}
                      </RecentList>
                    </>
                  )}
                </>
              ) : (
                <EmptyText style={{ padding: '30px 0' }}>불만 현황을 불러오지 못했습니다.</EmptyText>
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default AttentionItems;

// --- Styled ---
const Container = styled.div``;
const TopRow = styled.div`display:flex;align-items:center;margin-bottom:14px;`;
const Title = styled.h1`font-size:18px;font-weight:600;margin:0;`;

const Tabs = styled.div`display:flex;gap:8px;border-bottom:1px solid ${({theme})=>theme.colors.border};margin-bottom:16px;`;
const TabBtn = styled.button`
  display:inline-flex;align-items:center;gap:4px;
  padding:10px 18px;border:none;background:none;
  font-size:13px;font-weight:${({$active})=>$active?600:400};
  color:${({$active,theme})=>$active?theme.colors.primary:theme.colors.textSecondary};
  border-bottom:2px solid ${({$active,theme})=>$active?theme.colors.primary:'transparent'};
  margin-bottom:-1px;cursor:pointer;
`;
const TabCount = styled.span`font-size:12px;color:${({theme})=>theme.colors.textSecondary};font-weight:400;margin-left:2px;`;

const SectionNote = styled.div`
  padding:12px 14px;
  background:#F8FAFC;
  border:1px solid #E2E8F0;
  border-radius:${({theme})=>theme.radius.sm};
  color:#475569;
  font-size:13px;
  line-height:1.5;
  margin-bottom:14px;
  b{font-weight:600;color:#0F172A;}
`;

const CardList = styled.div`display:flex;flex-direction:column;gap:8px;`;
const PartnerCard = styled.div`
  display:flex;align-items:flex-start;justify-content:space-between;gap:14px;
  padding:14px 16px;
  background:#fff;
  border:1px solid ${({theme})=>theme.colors.border};
  border-radius:${({theme})=>theme.radius.md};
  cursor:pointer;
  &:hover{border-color:${({theme})=>theme.colors.primary};}
`;
const CardLeft = styled.div`flex:1;`;
const PartnerName = styled.div`font-size:14px;font-weight:600;color:${({theme})=>theme.colors.text};margin-bottom:4px;`;
const PartnerSub = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};`;
const SuspendReason = styled.div`font-size:12px;color:#EF4444;margin-top:6px;`;

const CardRight = styled.div`display:flex;flex-direction:column;align-items:flex-end;gap:6px;`;
const PenaltyBadge = styled.span`
  padding:4px 10px;
  background:${({$critical})=>$critical?'#FEE2E2':'#FEF3C7'};
  color:${({$critical})=>$critical?'#B91C1C':'#92400E'};
  border-radius:12px;
  font-size:12px;
  font-weight:600;
  white-space:nowrap;
`;
const StatusBadge = styled.span`
  padding:3px 8px;
  background:#FEE2E2;
  color:#B91C1C;
  border-radius:4px;
  font-size:11px;
  font-weight:600;
`;

const OverdueBadge = styled.span`
  padding:4px 10px;
  background:#FFEDD5;
  color:#9A3412;
  border-radius:12px;
  font-size:12px;
  font-weight:600;
  white-space:nowrap;
`;

const OverdueAmount = styled.div`
  font-size:12px;
  color:#F97316;
  margin-top:6px;
  font-weight:600;
`;

const EmptyText = styled.div`
  text-align:center;
  padding:50px 0;
  font-size:13px;
  color:${({theme})=>theme.colors.textSecondary};
  background:#fff;
  border-radius:${({theme})=>theme.radius.md};
`;

// 모달
const ModalOverlay = styled.div`position:fixed;inset:0;background:rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;z-index:1000;`;
const ModalContent = styled.div`background:#fff;border-radius:${({theme})=>theme.radius.lg};width:100%;max-width:560px;max-height:90vh;overflow-y:auto;`;
const ModalHeader = styled.div`display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid ${({theme})=>theme.colors.border};`;
const ModalTitle = styled.h2`font-size:16px;font-weight:600;margin:0;`;
const XBtn = styled.button`background:none;border:none;cursor:pointer;display:inline-flex;align-items:center;`;
const ModalBody = styled.div`padding:22px;`;
const MetaGrid = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:14px;padding-bottom:16px;border-bottom:1px solid ${({theme})=>theme.colors.borderLight};`;
const MetaItem = styled.div``;
const MetaLabel = styled.div`font-size:11px;color:${({theme})=>theme.colors.textSecondary};margin-bottom:3px;`;
const MetaValue = styled.div`font-size:13px;color:${({theme})=>theme.colors.text};font-weight:500;`;

const SectionTitle = styled.h3`font-size:13px;font-weight:600;margin:18px 0 10px;color:${({theme})=>theme.colors.text};`;

const StatRow = styled.div`display:grid;grid-template-columns:repeat(5,1fr);gap:6px;`;
const StatCard = styled.div`padding:10px 8px;background:#F8FAFC;border-radius:${({theme})=>theme.radius.sm};text-align:center;`;
const StatLabel = styled.div`font-size:11px;color:${({theme})=>theme.colors.textSecondary};margin-bottom:4px;`;
const StatValue = styled.div`font-size:15px;font-weight:700;color:${({$color})=>$color||'#0F172A'};`;

const CategoryList = styled.div`display:flex;flex-wrap:wrap;gap:6px;`;
const CategoryChip = styled.span`
  padding:5px 10px;
  background:#EEF2FF;
  color:#4338CA;
  border-radius:12px;
  font-size:12px;
  b{font-weight:700;margin-left:3px;}
`;

const RecentList = styled.div`display:flex;flex-direction:column;gap:6px;`;
const RecentItem = styled.div`
  display:flex;align-items:flex-start;gap:10px;
  padding:10px 12px;
  background:#F8FAFC;
  border-radius:${({theme})=>theme.radius.sm};
  font-size:12px;
`;
const RecentDate = styled.span`color:${({theme})=>theme.colors.textSecondary};white-space:nowrap;`;
const RecentSummary = styled.span`flex:1;color:${({theme})=>theme.colors.text};line-height:1.4;`;
const RecentPenalty = styled.span`color:#EF4444;font-weight:600;white-space:nowrap;`;
